import type { Theme } from "@mui/material/styles";
import type { AppSx } from "../types";

const SEARCH_FIELD_WIDTH_PX = 280;

export type DataTableDensity = "compact" | "comfortable";

/** Per-density metrics; "compact" keeps the report-chat tables as they were. */
const DENSITY = {
  compact: { headH: "38px", rowH: "36px", padX: "12px", padY: "7px", headFont: "0.75rem", cellFont: "0.8125rem", pageH: "44px", pageFont: "0.8125rem" },
  comfortable: { headH: "48px", rowH: "56px", padX: "16px", padY: "12px", headFont: "0.8125rem", cellFont: "0.9375rem", pageH: "56px", pageFont: "0.875rem" },
} as const;

export function buildReportChatDataTableCustomStyles(
  theme: Theme,
  density: DataTableDensity = "compact"
) {
  const c = theme.palette.reportChat;
  const fg = c.assistantText;
  const d = DENSITY[density];
  const cellPadding = {
    paddingLeft: d.padX,
    paddingRight: d.padX,
    paddingTop: d.padY,
    paddingBottom: d.padY,
  };
  return {
    table: {
      style: {
        backgroundColor: c.appBg,
        tableLayout: "fixed" as const,
        width: "100%",
      },
    },
    headRow: {
      style: {
        backgroundColor: c.appBg,
        color: fg,
        minHeight: d.headH,
      },
    },
    headCells: {
      style: {
        backgroundColor: c.appBg,
        color: fg,
        fontWeight: 650,
        fontSize: d.headFont,
        ...(density === "comfortable" ? { letterSpacing: "0.02em" } : {}),
        ...cellPadding,
      },
    },
    rows: {
      style: {
        backgroundColor: c.appBg,
        color: fg,
        minHeight: d.rowH,
        ...(density === "comfortable" ? { borderBottomColor: c.border } : {}),
      },
    },
    cells: {
      style: {
        backgroundColor: c.appBg,
        color: fg,
        fontSize: d.cellFont,
        ...cellPadding,
      },
    },
    pagination: {
      style: {
        backgroundColor: c.appBg,
        color: fg,
        borderTop: `1px solid ${c.border}`,
        minHeight: d.pageH,
        fontSize: d.pageFont,
        paddingTop: "4px",
        paddingBottom: "4px",
      },
    },
    noData: {
      style: {
        backgroundColor: c.appBg,
        color: fg,
      },
    },
    progress: {
      style: {
        backgroundColor: c.appBg,
        color: fg,
      },
    },
  };
}

export interface DataTableComponentStyles {
  actionIcon: AppSx;
  actionIconColor: (darkMode: boolean) => AppSx;
  actionsCell: AppSx;
  root: (appBg: string) => AppSx;
  toolbarRow: AppSx;
  toolbarContainer: AppSx;
  toolbarSearchRow: (alignStart: boolean) => AppSx;
  searchCollapseWrapper: AppSx;
  searchIconButton: AppSx;
  searchExpandedBox: AppSx;
  searchFieldAdornmentIcon: AppSx;
  searchFieldCollapsed: AppSx;
  refreshWrapper: AppSx;
  refreshButton: AppSx;
  actionsColumnStyle: { paddingLeft: string; paddingRight: string };
  reportChatCustomStyles: ReturnType<typeof buildReportChatDataTableCustomStyles>;
}

export function buildDataTableComponentStyles(theme: Theme): DataTableComponentStyles {
  return {
    actionIcon: {
      p: 0.75,
      flexShrink: 0,
      borderRadius: 1.5,
      "& .material-icons, & .MuiIcon-root": { fontSize: "1.375rem" },
    },
    actionIconColor: () => ({ color: "action.active" }),
    actionsCell: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "flex-start",
      flexWrap: "nowrap",
      gap: 0.25,
      width: "100%",
      maxWidth: "100%",
      overflow: "hidden",
    },
    root: (appBg) => ({ width: "100%", bgcolor: appBg }),
    toolbarRow: { width: "100%" },
    toolbarContainer: {
      display: "flex",
      alignItems: "center",
      gap: 1,
      px: 1.25,
      py: 1,
      flexWrap: "nowrap",
      borderBottom: "1px solid",
      borderColor: "divider",
    },
    toolbarSearchRow: (alignStart) => ({
      display: "flex",
      alignItems: alignStart ? "flex-start" : "center",
      flexShrink: 0,
    }),
    searchCollapseWrapper: { minHeight: 36 },
    searchIconButton: { color: "action.active" },
    searchExpandedBox: {
      width: SEARCH_FIELD_WIDTH_PX,
      maxWidth: "100%",
      transition: theme.transitions.create(["opacity", "transform"], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.shorter,
      }),
    },
    searchFieldAdornmentIcon: {
      color: "action.active",
      opacity: 0.85,
      pointerEvents: "none",
    },
    searchFieldCollapsed: {
      width: SEARCH_FIELD_WIDTH_PX,
      flexShrink: 0,
      "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
    },
    refreshWrapper: {
      ml: "auto",
      display: "flex",
      alignItems: "center",
      flexShrink: 0,
    },
    refreshButton: { color: "action.active" },
    actionsColumnStyle: { paddingLeft: "10px", paddingRight: "8px" },
    reportChatCustomStyles: buildReportChatDataTableCustomStyles(theme, "comfortable"),
  };
}
